import type { ReactNode } from "react"
import { createContext, useContext, useEffect, useMemo, useRef } from "react"

import type { ThreadLockSocket } from "@/lib/sockets/thread-lock"
import { useSession } from "@/lib/session"
import { createThreadLockSocket } from "@/lib/sockets/thread-lock"
import { useThreadLockState } from "@/lib/states/thread-lock-state"

const ThreadLockContext = createContext<ThreadLockSocket | null>(null)

/**
 * One socket per tab, so every screen shares the same set of held threads.
 */
export function ThreadLockProvider({ children }: { children: ReactNode }) {
	const { session } = useSession()
	const setLocks = useThreadLockState((s) => s.setLocks)
	const socketRef = useRef<ThreadLockSocket | null>(null)

	const workspaceId = session?.workspaceId
	const userId = session?.userId
	const name = session?.name
	const imageUrl = session?.imageUrl

	useEffect(() => {
		if (!workspaceId || !userId) return

		const socket = createThreadLockSocket({
			identity: { workspaceId, userId, name: name ?? "A teammate", imageUrl },
			onState: ({ locks }) => setLocks(locks),
		})
		socketRef.current = socket

		return () => {
			socketRef.current = null
			socket.close()
			setLocks([])
		}
	}, [workspaceId, userId, name, imageUrl, setLocks])

	const value = useMemo<ThreadLockSocket>(
		() => ({
			claim: (threadId) => socketRef.current?.claim(threadId),
			release: (threadId) => socketRef.current?.release(threadId),
			close: () => socketRef.current?.close(),
		}),
		[],
	)

	return (
		<ThreadLockContext.Provider value={value}>
			{children}
		</ThreadLockContext.Provider>
	)
}

/** Holds the thread while this screen is mounted. */
export function useHoldThread(threadId: string | undefined) {
	const socket = useContext(ThreadLockContext)
	const setHeldThreadId = useThreadLockState((s) => s.setHeldThreadId)

	useEffect(() => {
		if (!socket || !threadId) return
		socket.claim(threadId)
		setHeldThreadId(threadId)

		return () => {
			socket.release(threadId)
			setHeldThreadId(null)
		}
	}, [socket, threadId, setHeldThreadId])
}
